import { useEffect, useState } from "react";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";

export default function VerifyEmail() {
  const { user, setUser, logout } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [wait, setWait] = useState(60);
  useEffect(() => { if (wait <= 0) return undefined; const t = setTimeout(() => setWait(wait - 1), 1000); return () => clearTimeout(t); }, [wait]);

  const verify = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/verify-email", { code });
      toast.success(data.trial_started ? "Email verified! Your 7-day LearnAI Plus trial has started." : "Email verified!");
      setUser(data.user);
    } catch (e2) { setErr(formatError(e2)); setBusy(false); }
  };
  const resend = async () => {
    try { await api.post("/auth/resend-code"); toast.success("A new code is on its way"); setWait(60); setCode(""); setErr(""); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="verify-email-page" className="grid min-h-screen place-items-center bg-canvas px-4">
      <div className="w-full max-w-md rounded-3xl border border-line bg-surface p-8 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between"><Logo className="h-9" /><AppearanceMenu /></div>
        <span className="mt-8 grid h-12 w-12 place-items-center rounded-2xl bg-aqua-soft text-aqua"><MailCheck className="h-6 w-6" /></span>
        <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-ink">Verify your email</h1>
        <p className="mt-2 text-sm text-ink-2">We sent a 6-digit code to <b data-testid="verify-email-address">{user.email}</b>. It expires in 15 minutes. Check your spam folder too.</p>
        <form onSubmit={verify} className="mt-6 flex flex-col gap-4">
          <Input data-testid="verify-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="h-14 text-center font-mono text-2xl tracking-[0.5em]" />
          {err && <p data-testid="verify-error" className="text-sm text-red-600">{err}</p>}
          <Button data-testid="verify-submit-btn" type="submit" disabled={code.length !== 6 || busy} className="h-11 rounded-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify and continue"}
          </Button>
        </form>
        <div className="mt-5 flex items-center justify-between text-sm">
          <button data-testid="verify-resend-btn" type="button" disabled={wait > 0} onClick={resend} className="font-semibold text-aqua disabled:text-ink-3">
            {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
          </button>
          <button data-testid="verify-logout-btn" type="button" onClick={logout} className="text-ink-3 hover:underline">Use a different email</button>
        </div>
      </div>
    </div>
  );
}
